import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Typography,
  Box,
  Divider,
  SelectChangeEvent,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';

import { useUpdateWidget, useDeleteWidget } from '../../data/canvas/useWidgets';
import {
  Widget,
  UpdateWidgetRequest,
  Canvas,
  Page,
} from '../../types/canvas.types';
import { useEntityFields } from '../../data/canvas/useDatastoreSchemas';
import { DatastoreEntitySelector } from './DatastoreEntitySelector';

interface WidgetConfigDialogProps {
  open: boolean;
  onClose: () => void;
  widget: Widget;
  canvas: Canvas;
  page: Page;
  accountId: string;
  onDelete?: () => void;
}

const CHART_TYPES = [
  { value: 'line', label: 'Line Chart', description: 'Time series and trends' },
  { value: 'bar', label: 'Bar Chart', description: 'Categorical comparison' },
  {
    value: 'pie',
    label: 'Pie Chart',
    description: 'Part-to-whole relationships',
  },
  {
    value: 'scatter',
    label: 'Scatter Plot',
    description: 'Correlation analysis',
  },
  { value: 'area', label: 'Area Chart', description: 'Cumulative time series' },
];

export const WidgetConfigDialog: React.FC<WidgetConfigDialogProps> = ({
  open,
  onClose,
  widget,
  canvas,
  page,
  accountId,
  onDelete,
}) => {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [config, setConfig] = useState({
    title: widget.configuration?.title || '',
    description: widget.configuration?.description || '',
    chart_type: widget.configuration?.['chart_type'] || 'line',
    x_field: widget.configuration?.['x_field'] || '',
    y_field: widget.configuration?.['y_field'] || '',
    category_field: widget.configuration?.['category_field'] || '',
    show_legend: widget.configuration?.['show_legend'] ?? true,
  });

  const [imageConfig, setImageConfig] = useState({
    image_url: widget.configuration?.['image_url'] || '',
    alt_text: widget.configuration?.['alt_text'] || '',
    caption: widget.configuration?.['caption'] || '',
    fit_mode: (widget.configuration?.['fit_mode'] as string) || 'contain',
    lightbox_enabled: widget.configuration?.['lightbox_enabled'] !== false,
  });

  const [controlConfig, setControlConfig] = useState({
    filters:
      (widget.configuration?.['filters'] as Array<{
        field: string;
        label: string;
        type: 'select' | 'multi-select' | 'text' | 'date' | 'date-range';
        options?: Array<{ label: string; value: any }>;
      }>) || [],
  });

  const [entityName, setEntityName] = useState(
    widget.data_source?.['entity_name'] || '',
  );

  // Determine the effective datastore ID (page override or canvas default)
  const effectiveDatastoreId = page.datastore_id || canvas.datastore_id || '';

  const updateWidgetMutation = useUpdateWidget(
    widget.id,
    widget.page_id,
    accountId,
  );

  // Fetch available fields from the selected entity
  const { fields: availableFields } = useEntityFields(
    accountId,
    effectiveDatastoreId,
    entityName,
  );

  const handleSave = async () => {
    const updateData: UpdateWidgetRequest = {
      configuration: {
        ...widget.configuration,
        ...config,
        ...(widget.widget_type === 'control' && {
          filters: controlConfig.filters,
        }),
        ...(widget.widget_type === 'image' && imageConfig),
      },
      data_source: {
        datastore_id: effectiveDatastoreId,
        entity_name: entityName,
      },
    };

    try {
      await updateWidgetMutation.mutateAsync(updateData);
      onClose();
    } catch (error) {
      console.error('Error updating widget:', error);
    }
  };

  const handleCancel = () => {
    // Reset to original values
    setConfig({
      title: widget.configuration?.title || '',
      description: widget.configuration?.description || '',
      chart_type: widget.configuration?.['chart_type'] || 'line',
      x_field: widget.configuration?.['x_field'] || '',
      y_field: widget.configuration?.['y_field'] || '',
      category_field: widget.configuration?.['category_field'] || '',
      show_legend: widget.configuration?.['show_legend'] ?? true,
    });
    setEntityName(widget.data_source?.['entity_name'] || '');
    setControlConfig({
      filters:
        (widget.configuration?.['filters'] as Array<{
          field: string;
          label: string;
          type: 'select' | 'multi-select' | 'text' | 'date' | 'date-range';
          options?: Array<{ label: string; value: any }>;
        }>) || [],
    });
    setImageConfig({
      image_url: widget.configuration?.['image_url'] || '',
      alt_text: widget.configuration?.['alt_text'] || '',
      caption: widget.configuration?.['caption'] || '',
      fit_mode: (widget.configuration?.['fit_mode'] as string) || 'contain',
      lightbox_enabled: widget.configuration?.['lightbox_enabled'] !== false,
    });
    onClose();
  };

  const isLoading = updateWidgetMutation.isPending;
  const error = updateWidgetMutation.error;
  const isChartWidget = widget.widget_type === 'chart';
  const isControlWidget = widget.widget_type === 'control';
  const isImageWidget = widget.widget_type === 'image';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Configure Widget: {widget.configuration?.title || 'Untitled'}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Basic Configuration */}
          <Typography variant="h6" gutterBottom>
            Basic Information
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Widget Title"
                value={config.title}
                onChange={(e) =>
                  setConfig((prev) => ({ ...prev, title: e.target.value }))
                }
                placeholder="Enter widget title"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description (Optional)"
                value={config.description}
                onChange={(e) =>
                  setConfig((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Enter widget description"
                multiline
                rows={2}
              />
            </Grid>
          </Grid>

          {/* Data Source Configuration (not shown for content-only widgets like image) */}
          {!isImageWidget && (
            <>
              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                Data Source
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Datastore:{' '}
                <strong>
                  {page.datastore_id
                    ? `${page.title} (page override)`
                    : canvas.datastore_id
                      ? `${canvas.name} (canvas default)`
                      : 'No datastore configured'}
                </strong>
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <DatastoreEntitySelector
                    accountId={accountId}
                    datastoreId={effectiveDatastoreId}
                    value={entityName}
                    onChange={setEntityName}
                  />
                </Grid>
              </Grid>

              {availableFields && availableFields.length > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {availableFields.length} fields available:{' '}
                  {availableFields.slice(0, 5).join(', ')}
                  {availableFields.length > 5 &&
                    `, +${availableFields.length - 5} more`}
                </Alert>
              )}
            </>
          )}

          {/* Chart-specific Configuration */}
          {isChartWidget && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Chart Configuration
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Chart Type</InputLabel>
                    <Select
                      value={config.chart_type}
                      onChange={(e: SelectChangeEvent) =>
                        setConfig((prev) => ({
                          ...prev,
                          chart_type: e.target.value,
                        }))
                      }
                      label="Chart Type"
                    >
                      {CHART_TYPES.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          <Box>
                            <Typography variant="body1">
                              {type.label}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {type.description}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Show Legend</InputLabel>
                    <Select
                      value={config.show_legend ? 'true' : 'false'}
                      onChange={(e: SelectChangeEvent) =>
                        setConfig((prev) => ({
                          ...prev,
                          show_legend: e.target.value === 'true',
                        }))
                      }
                      label="Show Legend"
                    >
                      <MenuItem value="true">Yes</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl
                    fullWidth
                    disabled={!availableFields || !availableFields.length}
                  >
                    <InputLabel>X-Axis Field</InputLabel>
                    <Select
                      value={config.x_field}
                      onChange={(e: SelectChangeEvent) =>
                        setConfig((prev) => ({
                          ...prev,
                          x_field: e.target.value,
                        }))
                      }
                      label="X-Axis Field"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {availableFields?.map((field) => (
                        <MenuItem key={field} value={field}>
                          {field}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl
                    fullWidth
                    disabled={!availableFields || !availableFields.length}
                  >
                    <InputLabel>Y-Axis Field</InputLabel>
                    <Select
                      value={config.y_field}
                      onChange={(e: SelectChangeEvent) =>
                        setConfig((prev) => ({
                          ...prev,
                          y_field: e.target.value,
                        }))
                      }
                      label="Y-Axis Field"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {availableFields?.map((field) => (
                        <MenuItem key={field} value={field}>
                          {field}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl
                    fullWidth
                    disabled={!availableFields || !availableFields.length}
                  >
                    <InputLabel>Category Field (Optional)</InputLabel>
                    <Select
                      value={config.category_field}
                      onChange={(e: SelectChangeEvent) =>
                        setConfig((prev) => ({
                          ...prev,
                          category_field: e.target.value,
                        }))
                      }
                      label="Category Field (Optional)"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {availableFields?.map((field) => (
                        <MenuItem key={field} value={field}>
                          {field}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Chart Type Recommendations:</strong>
                </Typography>
                <Typography variant="caption" component="div">
                  • Line/Area: Use date/time for X-axis, numeric for Y-axis
                  <br />
                  • Bar: Use categories for X-axis, numeric for Y-axis
                  <br />
                  • Pie: Use categories for X-axis, numeric for Y-axis
                  <br />• Scatter: Use numeric for both X and Y, optional
                  category for color
                </Typography>
              </Alert>
            </>
          )}

          {/* Image Widget Configuration */}
          {isImageWidget && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Image Configuration
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Image URL"
                    value={imageConfig.image_url}
                    onChange={(e) =>
                      setImageConfig((prev) => ({
                        ...prev,
                        image_url: e.target.value,
                      }))
                    }
                    placeholder="https://example.com/image.png"
                    helperText="External URL or blobstore signed URL"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Alt Text"
                    value={imageConfig.alt_text}
                    onChange={(e) =>
                      setImageConfig((prev) => ({
                        ...prev,
                        alt_text: e.target.value,
                      }))
                    }
                    placeholder="Describe the image"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Caption (Optional)"
                    value={imageConfig.caption}
                    onChange={(e) =>
                      setImageConfig((prev) => ({
                        ...prev,
                        caption: e.target.value,
                      }))
                    }
                    placeholder="Caption text"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Fit Mode</InputLabel>
                    <Select
                      value={imageConfig.fit_mode}
                      label="Fit Mode"
                      onChange={(e: SelectChangeEvent) =>
                        setImageConfig((prev) => ({
                          ...prev,
                          fit_mode: e.target.value,
                        }))
                      }
                    >
                      <MenuItem value="contain">Contain</MenuItem>
                      <MenuItem value="cover">Cover</MenuItem>
                      <MenuItem value="fill">Fill</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Lightbox</InputLabel>
                    <Select
                      value={imageConfig.lightbox_enabled ? 'true' : 'false'}
                      label="Lightbox"
                      onChange={(e: SelectChangeEvent) =>
                        setImageConfig((prev) => ({
                          ...prev,
                          lightbox_enabled: e.target.value === 'true',
                        }))
                      }
                    >
                      <MenuItem value="true">Enabled</MenuItem>
                      <MenuItem value="false">Disabled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {imageConfig.image_url && (
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Preview
                    </Typography>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 1,
                        maxHeight: 200,
                        overflow: 'hidden',
                        display: 'flex',
                        justifyContent: 'center',
                        bgcolor: 'grey.50',
                      }}
                    >
                      <Box
                        component="img"
                        src={imageConfig.image_url}
                        alt={imageConfig.alt_text || 'Preview'}
                        sx={{
                          maxWidth: '100%',
                          maxHeight: 180,
                          objectFit: 'contain',
                        }}
                        onError={(e: any) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </>
          )}

          {/* Control Widget Configuration */}
          {isControlWidget && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Control Filters Configuration
              </Typography>

              {controlConfig.filters.map((filter, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 3,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        fullWidth
                        label="Field Name"
                        value={filter.field}
                        onChange={(e) => {
                          const newFilters = [...controlConfig.filters];
                          newFilters[index].field = e.target.value;
                          setControlConfig({ filters: newFilters });
                        }}
                        placeholder="e.g., region"
                        size="small"
                      />
                    </Grid>

                    <Grid item xs={12} sm={5}>
                      <TextField
                        fullWidth
                        label="Display Label"
                        value={filter.label}
                        onChange={(e) => {
                          const newFilters = [...controlConfig.filters];
                          newFilters[index].label = e.target.value;
                          setControlConfig({ filters: newFilters });
                        }}
                        placeholder="e.g., Region"
                        size="small"
                      />
                    </Grid>

                    <Grid item xs={12} sm={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={filter.type}
                          label="Type"
                          onChange={(e: SelectChangeEvent) => {
                            const newFilters = [...controlConfig.filters];
                            newFilters[index].type = e.target.value as any;
                            setControlConfig({ filters: newFilters });
                          }}
                        >
                          <MenuItem value="select">Select</MenuItem>
                          <MenuItem value="multi-select">Multi-Select</MenuItem>
                          <MenuItem value="text">Text</MenuItem>
                          <MenuItem value="date">Date</MenuItem>
                          <MenuItem value="date-range">Date Range</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {(filter.type === 'select' ||
                      filter.type === 'multi-select') && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Options (comma-separated)"
                          placeholder="e.g., North America, Europe, Asia"
                          helperText="Enter values separated by commas"
                          size="small"
                          defaultValue={
                            filter.options?.map((o) => o.label).join(', ') || ''
                          }
                          onChange={(e) => {
                            const values = e.target.value
                              .split(',')
                              .map((v) => v.trim())
                              .filter((v) => v);
                            const newFilters = [...controlConfig.filters];
                            newFilters[index].options = values.map((v) => ({
                              label: v,
                              value: v,
                            }));
                            setControlConfig({ filters: newFilters });
                          }}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => {
                          const newFilters = controlConfig.filters.filter(
                            (_, i) => i !== index,
                          );
                          setControlConfig({ filters: newFilters });
                        }}
                      >
                        Remove Filter
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              ))}

              <Button
                variant="outlined"
                onClick={() => {
                  setControlConfig({
                    filters: [
                      ...controlConfig.filters,
                      { field: '', label: '', type: 'select', options: [] },
                    ],
                  });
                }}
              >
                + Add Filter
              </Button>
            </>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Error updating widget: {error?.message}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {onDelete && (
          <Button
            onClick={() => setDeleteConfirmOpen(true)}
            color="error"
            startIcon={<DeleteIcon />}
            disabled={isLoading}
          >
            Delete Widget
          </Button>
        )}
        <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
          <Button onClick={handleCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!config.title.trim() || isLoading}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Save Changes'
            )}
          </Button>
        </Box>
      </DialogActions>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="sm"
      >
        <DialogTitle>Delete Widget?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the widget{' '}
            <strong>{config.title || 'Untitled Widget'}</strong>? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              setDeleteConfirmOpen(false);
              onClose();
              if (onDelete) {
                onDelete();
              }
            }}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};
